import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Alert, Center, Loader } from '@mantine/core';
import { CreditCard } from 'lucide-react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { BillingViewModel } from '@/features/billing/BillingViewModel';
import { CardForm } from '@/features/billing/CardForm';
import { StepShell } from './StepShell';

/**
 * Inline payment step (SaaS only) — captures a card via the shared Stripe
 * `CardForm` (Payment Element). Optional; the wizard's Next handles skipping.
 */
export function OnboardingPaymentStep({ onSaved }: { onSaved: () => void }): ReactElement {
    const vm = useViewModel(BillingViewModel);
    const [cardSecret, setCardSecret] = useState<string | null>(null);

    useEffect(() => { if (!vm.overview) void vm.load(); }, [vm]);

    const pk = vm.overview?.publishableKey ?? null;
    const stripePromise = useMemo<Promise<Stripe | null> | null>(() => (pk ? loadStripe(pk) : null), [pk]);

    const needsCard = Boolean(vm.overview) && !vm.overview?.card && Boolean(stripePromise);
    useEffect(() => {
        if (needsCard && !cardSecret) {
            void vm.createSetupIntent().then((r) => setCardSecret(r.clientSecret)).catch(() => undefined);
        }
    }, [needsCard, cardSecret, vm]);

    return (
        <StepShell
            icon={<CreditCard size={20} />}
            title="Add a payment method"
            description="Save a card to enable your subscription. You can also do this later from Billing."
        >
            {vm.loading && !vm.overview ? (
                <Center py="md"><Loader size="sm" /></Center>
            ) : vm.overview?.card ? (
                <Alert color="green" variant="light">A card is already on file (•••• {vm.overview.card.last4}). You&apos;re all set — continue.</Alert>
            ) : !stripePromise ? (
                <Alert color="blue" variant="light">Payment isn&apos;t set up by your provider yet — you can skip this step.</Alert>
            ) : cardSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret: cardSecret, appearance: { theme: 'stripe' } }}>
                    <CardForm vm={vm} onDone={onSaved} saveLabel="Save card & continue" />
                </Elements>
            ) : (
                <Center py="md"><Loader size="sm" /></Center>
            )}
        </StepShell>
    );
}
